import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Bank, Coins, Wallet, PencilSimple, Trash, HandCoins, UserMinus, PiggyBank, Buildings, Eye, CreditCard } from '@phosphor-icons/react';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export const ACCOUNT_TYPE_META = {
  cash:        { label: 'Cash',    icon: Coins,      bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', tint: 'bg-[#FFFBEB]', barBg: 'bg-[#D97706]', side: 'asset' },
  bank:        { label: 'Bank',    icon: Bank,       bg: 'bg-[#DBEAFE]', text: 'text-[#2563EB]', tint: 'bg-[#EFF6FF]', barBg: 'bg-[#2563EB]', side: 'asset' },
  wallet:      { label: "W'let",   icon: Wallet,     bg: 'bg-[#EDE9FE]', text: 'text-[#7C3AED]', tint: 'bg-[#F5F3FF]', barBg: 'bg-[#7C3AED]', side: 'asset' },
  debtor:      { label: 'Debtor',  icon: HandCoins,  bg: 'bg-[#FCE7F3]', text: 'text-[#DB2777]', tint: 'bg-[#FDF2F8]', barBg: 'bg-[#DB2777]', side: 'asset' },
  asset:       { label: 'Asset',   icon: Buildings,  bg: 'bg-[#E0E7FF]', text: 'text-[#4F46E5]', tint: 'bg-[#EEF2FF]', barBg: 'bg-[#4F46E5]', side: 'asset' },
  creditor:    { label: 'Creditor',icon: UserMinus,  bg: 'bg-[#FFE4E6]', text: 'text-[#DC2626]', tint: 'bg-[#FEF2F2]', barBg: 'bg-[#DC2626]', side: 'liability' },
  capital:     { label: 'Capital', icon: PiggyBank,  bg: 'bg-[#DCFCE7]', text: 'text-[#16A34A]', tint: 'bg-[#F0FDF4]', barBg: 'bg-[#16A34A]', side: 'liability' },
  credit_card: { label: 'CredCard',icon: CreditCard, bg: 'bg-[#FFE4F1]', text: 'text-[#BE185D]', tint: 'bg-[#FDF2F8]', barBg: 'bg-[#BE185D]', side: 'liability' },
};

export const typeMeta = (t) => ACCOUNT_TYPE_META[t] || ACCOUNT_TYPE_META.cash;

export function AccountCard({ acc, onEdit, onDelete, onView }) {
  const meta = typeMeta(acc.type);
  const Icon = meta.icon;
  // Liability-side accounts (creditor / capital) sit on the credit column of the
  // ledger — they're "negative by default" in the books. The Balance Sheet shows
  // them as positive figures (the absolute value). Only flip to red if the value
  // genuinely went the wrong way (creditor turning into a debit balance, etc.).
  const isLiabilitySide = meta.side === 'liability';
  const displayBalance = isLiabilitySide ? Math.abs(acc.balance) : acc.balance;
  const isAbnormal = isLiabilitySide ? acc.balance > 0 : acc.balance < 0;
  return (
    <Card className={`rounded-xl border border-[#E4E4E7] shadow-sm ${meta.tint}`} data-testid={`account-${acc.id}`}>
      <CardContent className="p-3">
        {/* Top row: icon · actions (type label hidden — group bar already shows the type) */}
        <div className="flex items-center justify-between mb-2">
          <div className={`w-6 h-6 rounded-md ${meta.bg} flex items-center justify-center`}>
            <Icon size={12} className={meta.text} />
          </div>
          <div className="flex gap-0.5">
            {onView && (
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-[#52525B] hover:text-[#0A0A0A]" onClick={() => onView(acc)} data-testid={`view-acc-${acc.id}`}>
                <Eye size={11} />
              </Button>
            )}
            {onEdit && (
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-[#52525B] hover:text-[#3B82F6]" onClick={() => onEdit(acc)} data-testid={`edit-acc-${acc.id}`}>
                <PencilSimple size={11} />
              </Button>
            )}
            {onDelete && (
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-[#52525B] hover:text-[#EF4444]" onClick={() => onDelete(acc)} data-testid={`delete-acc-${acc.id}`}>
                <Trash size={11} />
              </Button>
            )}
          </div>
        </div>
        {/* Bottom row: name + balance on same line */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-[11px] uppercase tracking-[0.1em] font-semibold text-[#52525B] truncate flex-1">{acc.name}</p>
          <p className={`text-base font-bold tracking-tight font-mono whitespace-nowrap ${isAbnormal ? 'text-[#DC2626]' : 'text-[#0A0A0A]'}`} style={{ fontFamily: 'var(--font-heading)' }}>
            {fmt(displayBalance)}
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
